import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import MegaMenuItemThirdLevel from './MegeMenuItemThirdLevel';

const MegaMenuItemSecondLevel = ({
  id,
  title = '',
  url = '',
  subtree = {},
  obfuscate = false,
  obfuscateDataUrl,
  attributes,
  disableForLanguage = false,
  classStyle = '',
  thirdLevelIsOpen,
  updateChildState,
}) => {
  const thirdLevelRef = useRef(null);

  useEffect(() => {
    const wrapAll = (ary, wrapper) => {
      if (ary && ary.length && wrapper) {
        for (let i = 0; i < ary.length; i++) {
          wrapper.appendChild(ary[i]);
        }
      }
    };

    const secondLevelNodes = document.querySelectorAll('.megamenu__item_second-level');
    const wrapper = document.querySelector('.megamenu__second-level-items-list');

    wrapAll(secondLevelNodes, wrapper);
  }, []);

  const toggleMenuThirdLevel = (event) => {
    // Keypresses other than Enter and Space should not trigger a command
    if (event) {
      if (
        event.type === "keydown" &&
        event.key !== "Enter" &&
        event.key !== " "
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
    }

    if (thirdLevelIsOpen) {
      updateChildState(null);
    } else {
      updateChildState(id);
    }
  };

  const renderLink = () => {
    let classFromBO = '';
    let targetFromBO = '';

    if (attributes && typeof attributes === 'object') {
      classFromBO = attributes.class || '';
      targetFromBO = attributes.target || '';
    }

    const hasChildren = subtree && Object.keys(subtree).length > 0;

    if (obfuscate && !hasChildren) {
      return (
        <span
          aria-label={title}
          role="button"
          tabIndex="0"
          className={cn('obfuscate', classFromBO)}
          data-link={obfuscateDataUrl}
          data-target={targetFromBO}
        >
          {title}
        </span>
      );
    }

    if (hasChildren) {
      return (
        <span
          aria-label={title}
          role="button"
          tabIndex="0"
          className={cn('hasChildren', { 'active': thirdLevelIsOpen })}
          onClick={toggleMenuThirdLevel}
          onKeyDown={toggleMenuThirdLevel}
        >
          {title}
        </span>
      );
    }

    return (
      <a
        aria-label={title}
        tabIndex="0"
        href={url}
        className={cn(classFromBO)}
        target={targetFromBO}
      >
        {title}
      </a>
    );
  };

  if (disableForLanguage) return null;

  const items = Object.keys(subtree || {}).map((key) => (
    <MegaMenuItemThirdLevel key={key} {...subtree[key]} />
  ));

  const columnCount = 3;
  const minItemsPerColumn = 6;
  const itemsPerColumn = Math.max(minItemsPerColumn, Math.ceil(items.length / columnCount));
  const columns = [];

  for (let i = 0; i < columnCount; i++) {
    const startIndex = i * itemsPerColumn;
    const endIndex = Math.min((i + 1) * itemsPerColumn, items.length);
    const columnItems = items.slice(startIndex, endIndex);
    if (columnItems.length > 0) {
      columns.push(
        <div className="megamenu__third-level-col" key={i}>
          {columnItems}
        </div>
      );
    }
  }

  return (
    <div
      ref={thirdLevelRef}
      className={cn('megamenu__third-level-container', { 'active': thirdLevelIsOpen })}
    >
      <div className={cn('megamenu__item megamenu__item_second-level', classStyle)}>
        {renderLink()}
      </div>

      {thirdLevelIsOpen && columns}
    </div>
  );
};

MegaMenuItemSecondLevel.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string,
  url: PropTypes.string.isRequired,
  subtree: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  obfuscate: PropTypes.bool,
  obfuscateDataUrl: PropTypes.string,
  attributes: PropTypes.object,
  disableForLanguage: PropTypes.bool,
  classStyle: PropTypes.string,
  thirdLevelIsOpen: PropTypes.bool,
  updateChildState: PropTypes.func,
};

export default React.memo(MegaMenuItemSecondLevel);
